/**
 * A load that failed, and the button that tries it again (OPS-002, PLAN §7).
 *
 * Every page had an error message before this pass and none of them had a way
 * out of it: the only recovery was a browser reload, which throws away the
 * scroll position and every other panel on the page that loaded fine. A
 * transient 500 or a worker restart is the common case, and one press should
 * be enough.
 *
 * `role="alert"` on the message so a screen reader hears it without the page
 * being re-announced, and the retry is a real button rather than a link so it
 * cannot be opened in a new tab as a navigation.
 *
 * `level` because this component is both a whole page and a panel inside one.
 * When it *is* the page — a profile that did not load is still the profile
 * route — its title is the page's heading and belongs at level 1; when it sits
 * under a page that already has one, it stays at 2. Default 2, so a caller has
 * to mean it.
 */
export default function ErrorState({
  title = 'That did not load',
  message,
  onRetry,
  retryLabel = 'Try again',
  busy = false,
  level = 2,
}) {
  const Heading = level === 1 ? 'h1' : 'h2'
  return (
    <div className="card card-pad">
      <div className="empty-state">
        <Heading>{title}</Heading>
        {/*
          A problem+json `detail` often reads as the same sentence the page
          uses for its heading, and printing both is a page that says one thing
          twice. Seen in the browser on the charts route, where "The charts did
          not load" appeared above "The charts did not load."
        */}
        <p role="alert">
          {message && message.replace(/\.$/, '') !== title.replace(/\.$/, '')
            ? message
            : 'Something went wrong on our side. This is usually temporary.'}
        </p>
        {onRetry ? (
          <button type="button" className="btn btn-tonal" onClick={onRetry} disabled={busy}>
            {busy ? 'Trying…' : retryLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The waiting state, as the shape of the thing that is coming.
 *
 * Three grey bars rather than the word "Loading…": a skeleton the size of the
 * content stops the page jumping when it arrives, which is the whole reason
 * PLAN §7 asks for skeletons by name. `role="status"` with the text in a
 * visually-hidden span, because an animated grey rectangle is not an
 * announcement.
 */
export function LoadingState({ label = 'Loading', rows = 3 }) {
  return (
    <div className="card card-pad" role="status">
      <span className="sr-only">{label}</span>
      <div className="skeleton-stack" aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="skeleton skeleton-row" />
        ))}
      </div>
    </div>
  )
}
